import { ErrorBoundary } from "@sentry/react";
import { Suspense } from "react";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { TicketCategoryList } from "../components/TicketCategoryList";
import { canManageCategories } from "../permissions";

export function TicketCategoryOverviewPage(): React.ReactNode {
  const projectId = useProjectId();
  const slug = useSlug();
  const { t } = useTranslation();
  const hasPermission = usePermission();

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.repair-category.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.tickets.title"),
              to: routes.tickets.list({
                slug,
              }),
            },
            {
              name: t("page.repair-category.list.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{ scope: "project", entity: "ticket-categories", page: "list" }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.ticket-categories.suggestion.1"),
              t("component.ask-nobi-chat-drawer.ticket-categories.suggestion.2"),
            ]}
          />
          {hasPermission(canManageCategories) ? (
            <Button
              type="link"
              href={routes.ticketCategories.create({
                slug,
              })}
              data-testid="create-category-link"
            >
              {t("model.repair-category.action.create")}
            </Button>
          ) : null}
        </>
      }
    >
      <ErrorBoundary fallback={(error) => <ErrorPage error={error} />}>
        <Suspense fallback={<FullSizeLoader withPadding />}>
          <TicketCategoryList />
        </Suspense>
      </ErrorBoundary>
    </DocumentPage>
  );
}
